<script setup>
import { computed } from 'vue'
import { state, params, selected, getObsSpecs, actions } from '@/components/Ajuda/Montador/composables/useBuilderState'
import DecimalField from '@/components/Ajuda/Montador/components/Fields/DecimalField.vue'
import { parseNumberList, normalizeListInputLive, finalizeListInput, heightsToGaps } from '@/components/Ajuda/Montador/lib/numbers'

/* ===== Helpers locais ===== */
const selType = computed(() => (selected.value?.type || '').toLowerCase())
const isOxer = computed(() => selType.value === 'oxer')
const isTriple = computed(() => selType.value === 'triplebar' || selType.value === 'triple')

// usando globais? (sem overrides)
const usingGlobal = computed(() => {
  const o = selected.value
  if (!o) return true
  return !o.local || Object.keys(o.local).length === 0
})

function applyGlobalsToSelected() {
  const o = selected.value
  if (!o) return
  const p = params.obstacles?.[o.type] || {}
  o.local = o.local || {}
  // dimensões básicas
  if (p.wm != null) o.local.wm = p.wm
  if (p.hm != null) o.local.hm = p.hm
  if (p.dir != null) o.local.dir = p.dir
  // listas
  if (Array.isArray(p.thicknesses)) o.local.thicknesses = [...p.thicknesses]
  if (Array.isArray(p.gaps))        o.local.gaps        = [...p.gaps]
  if (Array.isArray(p.heights))     o.local.heights     = [...p.heights]
  else if (Array.isArray(p.barHeights)) o.local.heights = [...p.barHeights]
  // sincroniza inputs editáveis
  actions.setEditsFromSelected()
}

function toggleUseGlobal(val) {
  const o = selected.value
  if (!o) return
  if (val) {
    // voltar a usar globais => limpa overrides
    o.local = {}
    actions.setEditsFromSelected()
  } else {
    // começar a editar local => preenche com globais
    applyGlobalsToSelected()
  }
}

// normalização live + ao sair do campo (usa numbers.js)
function onListInput(which, ev) {
  const v = normalizeListInputLive(ev?.target?.value ?? '')
  if (which === 'thicks')  state.editLists.thicks  = v
  if (which === 'heights') state.editLists.heights = v
  if (which === 'gaps')    state.editLists.gaps    = v
}

function onListBlur(which) {
  if (which === 'thicks')  state.editLists.thicks  = finalizeListInput(state.editLists.thicks)
  if (which === 'heights') state.editLists.heights = finalizeListInput(state.editLists.heights)
  if (which === 'gaps')    state.editLists.gaps    = finalizeListInput(state.editLists.gaps)
}

// validação rápida (só Oxer/Triple)
const listError = computed(() => {
  const o = selected.value
  if (!o || (!isOxer.value && !isTriple.value)) return ''
  const th = parseNumberList(state.editLists.thicks)
  const gp = parseNumberList(state.editLists.gaps)
  const ht = parseNumberList(state.editLists.heights)
  const needBars = isOxer.value ? 2 : 3
  if (th.length !== needBars) return `${isOxer.value ? 'Oxer' : 'Triple'} precisa de ${needBars} espessuras.`
  if (ht.length > 0 && ht.length !== needBars) return `Alturas precisa de ${needBars} valores.`
  if (ht.length === 0) {
    const needGaps = needBars - 1
    if (gp.length !== needGaps) return `${isOxer.value ? 'Oxer' : 'Triple'} precisa de ${needGaps} vão(ões).`
    if (gp.some(g => g < 0.30 || g > 2.00)) return 'Vãos devem estar entre 0,30 e 2,00 m.'
  }
  return ''
})

function deriveGapsFromHeights() {
  const o = selected.value
  if (!o) return
  const th = parseNumberList(state.editLists.thicks)
  const ht = parseNumberList(state.editLists.heights)
  if (!th.length || !ht.length || th.length !== ht.length) return
  const gp = heightsToGaps(ht, th)
  state.editLists.gaps = finalizeListInput(gp.join(';'))
}

function applyLists() {
  actions.applyEditsToSelected()
}

// rotacionar frente (dir: 1/-1)
function flipDir() {
  const o = selected.value
  if (!o) return
  const cur = getObsSpecs(o).dir
  o.local = o.local || {}
  o.local.dir = (cur === -1 ? 1 : -1)
}

const effSpecs = computed(() => selected.value ? getObsSpecs(selected.value) : null)
</script>

<template>
  <div class="section">
    <label class="label">Overrides do obstáculo</label>

    <div v-if="!selected">
      <div class="hint">Selecione um obstáculo para editar seus overrides locais.</div>
    </div>

    <template v-else>
      <!-- Alternância globais/locais -->
      <div class="row">
        <label class="checkbox">
          <input type="checkbox" :checked="usingGlobal" @change="toggleUseGlobal($event.target.checked)" />
          <span>Usar parâmetros globais do tipo</span>
        </label>
      </div>

      <!-- Dimensões básicas e frente -->
      <div class="row" style="margin-top:6px">
        <DecimalField v-model.number="selected.local.wm" :disabled="usingGlobal" :step="0.05" label="Largura (wm)" />
        <DecimalField v-model.number="selected.local.hm" :disabled="usingGlobal" :step="0.05" label="Altura (hm)" />
      </div>
      <div class="row">
        <button class="btn" :disabled="usingGlobal" @click="flipDir">Inverter frente (dir)</button>
        <div class="hint">Frente atual: {{ effSpecs?.dir === -1 ? '←' : '→' }}</div>
      </div>

      <!-- Oxer / Triple: listas -->
      <div v-if="isOxer || isTriple">
        <div class="row" style="margin-top:6px">
          <div class="flex-fill">
            <label class="label">Espessuras (m)</label>
            <input
              class="form-control"
              :disabled="usingGlobal"
              :value="state.editLists.thicks"
              @input="onListInput('thicks', $event)"
              @blur="onListBlur('thicks')"
              placeholder="ex.: 0.10;0.10;0.10"
            />
          </div>
        </div>

        <div class="row" style="margin-top:6px">
          <div class="flex-fill">
            <label class="label">Alturas (m)</label>
            <input
              class="form-control"
              :disabled="usingGlobal"
              :value="state.editLists.heights"
              @input="onListInput('heights', $event)"
              @blur="onListBlur('heights')"
              placeholder="ex.: 0.60;0.80;1.00"
            />
          </div>
          <div class="flex-fill">
            <label class="label">Vãos (m)</label>
            <input
              class="form-control"
              :disabled="usingGlobal"
              :value="state.editLists.gaps"
              @input="onListInput('gaps', $event)"
              @blur="onListBlur('gaps')"
              placeholder="ex.: 0.50;0.60"
            />
          </div>
        </div>

        <div class="row" style="margin-top:6px">
          <button class="btn" :disabled="usingGlobal" @click="deriveGapsFromHeights">Derivar vãos a partir das alturas</button>
          <button class="btn" :disabled="usingGlobal || !!listError" @click="applyLists">Aplicar no obstáculo</button>
        </div>

        <div v-if="listError" class="hint" style="color:#b00020">{{ listError }}</div>
        <div v-else class="hint">Use ponto ou vírgula nas casas decimais; separe valores com ponto e vírgula (;).</div>
      </div>

      <!-- Tipos simples: só espessura de barra (opcional) -->
      <div v-else style="margin-top:6px">
        <div class="row">
          <DecimalField
            v-model.number="selected.local.barThickness"
            :disabled="usingGlobal"
            :step="0.01"
            label="Espessura da barra (m)"
          />
        </div>
        <div class="hint">Para Vertical/Gate/Plank/Wall, apenas espessura e direção costumam ser suficientes.</div>
      </div>

      <!-- Ações rápidas -->
      <div class="row" style="margin-top:8px">
        <button class="btn" @click="applyGlobalsToSelected" :disabled="!selected">Prefill com globais</button>
        <button class="btn" @click="actions.setEditsFromSelected" :disabled="!selected">Recarregar listas atuais</button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.label { margin: 0 0 6px; font-size: 13px; color: #9eb3ff; }
</style>
